import type { OAuthProvider } from './types';

export type SsoProviderIcon = 'google' | 'microsoft' | 'generic';

export type SsoProviderConfig = {
  key: OAuthProvider;
  label: string;
  cognitoIdentityProvider: string;
  icon: SsoProviderIcon;
};

const BUILT_IN_SSO_PROVIDERS: SsoProviderConfig[] = [
  {
    key: 'google',
    label: 'Google',
    cognitoIdentityProvider: 'Google',
    icon: 'google',
  },
  {
    key: 'azure',
    label: 'Microsoft',
    cognitoIdentityProvider: 'Microsoft',
    icon: 'microsoft',
  },
];

const parseExtraSsoProviders = (): SsoProviderConfig[] => {
  const raw = import.meta.env.VITE_COGNITO_SSO_PROVIDERS;
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((entry): SsoProviderConfig | null => {
        if (!entry || typeof entry !== 'object') return null;
        const candidate = entry as Record<string, unknown>;
        const key = typeof candidate.key === 'string' ? candidate.key : '';
        const label =
          typeof candidate.label === 'string' ? candidate.label : key;
        const cognitoIdentityProvider =
          typeof candidate.cognitoIdentityProvider === 'string'
            ? candidate.cognitoIdentityProvider
            : typeof candidate.identityProvider === 'string'
              ? candidate.identityProvider
              : '';
        if (!key || !label || !cognitoIdentityProvider) return null;
        return {
          key,
          label,
          cognitoIdentityProvider,
          icon: 'generic',
        };
      })
      .filter((provider): provider is SsoProviderConfig => Boolean(provider));
  } catch {
    console.warn('Invalid VITE_COGNITO_SSO_PROVIDERS JSON');
    return [];
  }
};

export const getSsoProviders = (): SsoProviderConfig[] => [
  ...BUILT_IN_SSO_PROVIDERS,
  ...parseExtraSsoProviders(),
];

export const getCognitoIdentityProvider = (provider: OAuthProvider): string => {
  const configured = getSsoProviders().find(
    (candidate) => candidate.key === provider
  );
  return configured?.cognitoIdentityProvider || provider;
};
